import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  DollarSign, TrendingUp, TrendingDown, Bike, Plus, Trash2,
  ChevronDown, ChevronUp, Wrench, Calendar, Edit, ArrowUpRight, ArrowDownRight, Download
} from 'lucide-react';
import { apiService } from '../../services/api';

const formatCurrency = (val) => `R$ ${(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

export const FechamentoMensalPage = () => {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mesAtual, setMesAtual] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [expandedMotos, setExpandedMotos] = useState({});
  const [showDespesaFixaModal, setShowDespesaFixaModal] = useState(false);
  const [editingDespesa, setEditingDespesa] = useState(null);
  const [despesaForm, setDespesaForm] = useState({ nome: '', valor: '', descricao: '' });
  const [detalheModal, setDetalheModal] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSubAdmin = user?.tipo === 'sub_admin';

  useEffect(() => {
    carregarDados();
  }, [mesAtual]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const response = await apiService.api.get(`/fechamento/mensal?mes=${mesAtual}`);
      setDados(response.data);
    } catch (error) {
      console.error('Erro ao carregar fechamento:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadDrePdf = async () => {
    try {
      const response = await apiService.api.get(`/fechamento/dre-parceiro/pdf?mes=${mesAtual}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DRE_${mesAtual}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Erro ao gerar PDF: ' + (error.response?.data?.detail || error.message));
    }
  };

  const downloadRelatorioMensalPdf = async () => {
    try {
      const response = await apiService.api.get(`/fechamento/relatorio-pdf?mes=${mesAtual}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_mensal_${mesAtual}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Erro ao gerar relatório: ' + (error.response?.data?.detail || error.message));
    }
  };

  const salvarDespesaFixa = async () => {
    try {
      if (editingDespesa) {
        await apiService.api.put(`/despesas-fixas/${editingDespesa.id}`, despesaForm);
      } else {
        await apiService.api.post('/despesas-fixas', despesaForm);
      }
      setShowDespesaFixaModal(false);
      setEditingDespesa(null);
      setDespesaForm({ nome: '', valor: '', descricao: '' });
      carregarDados();
    } catch (error) {
      alert('Erro ao salvar: ' + (error.response?.data?.detail || error.message));
    }
  };

  const excluirDespesaFixa = async (id) => {
    if (!window.confirm('Excluir esta despesa fixa?')) return;
    try {
      await apiService.api.delete(`/despesas-fixas/${id}`);
      carregarDados();
    } catch (error) {
      alert('Erro ao excluir: ' + (error.response?.data?.detail || error.message));
    }
  };

  const navegarMes = (dir) => {
    const [ano, mes] = mesAtual.split('-').map(Number);
    const d = new Date(ano, mes - 1 + dir, 1);
    setMesAtual(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const mesLabel = useMemo(() => {
    const [ano, mes] = mesAtual.split('-');
    const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
    return `${meses[parseInt(mes) - 1]} ${ano}`;
  }, [mesAtual]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-2 border-emerald-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  const resumo = dados?.resumo || {};
  const motos = dados?.motos || [];
  const despesasFixas = dados?.despesas_fixas || [];
  const receitasNaoVinculadas = dados?.receitas_nao_vinculadas || [];
  const debitosPagosDetalhe = dados?.debitos_pagos_detalhe || [];
  const manutencoesDetalhe = dados?.manutencoes_detalhe || [];

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Fechamento Mensal</h1>
          <p className="text-sm text-gray-400">Consolidação receita, despesas e lucro por moto</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            data-testid="btn-download-relatorio-mensal"
            onClick={downloadRelatorioMensalPdf}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            title="Baixar relatório mensal completo em PDF"
          >
            <Download className="h-4 w-4 mr-1" />
            Relatório Mensal
          </Button>
          <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-1">
            <Button size="sm" variant="ghost" onClick={() => navegarMes(-1)} className="text-gray-400 hover:text-white">
              &lt;
            </Button>
            <span className="text-white font-semibold px-3 min-w-[100px] text-center">{mesLabel}</span>
            <Button size="sm" variant="ghost" onClick={() => navegarMes(1)} className="text-gray-400 hover:text-white">
              &gt;
            </Button>
          </div>
        </div>
      </div>

      {/* Cards de Resumo — clicáveis para detalhamento */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-emerald-600/20 to-emerald-800/20 border-emerald-500/30 cursor-pointer hover:border-emerald-400/50 transition-colors" onClick={() => setDetalheModal('receita')}>
          <CardContent className="p-4">
            <p className="text-xs text-emerald-300/80">Receita Total</p>
            <p className="text-xl font-bold text-emerald-300">{formatCurrency(resumo.total_receita)}</p>
            {resumo.total_recebido_bruto > 0 && resumo.total_recebido_bruto !== resumo.total_receita && (
              <p className="text-[10px] text-emerald-200/70 mt-0.5" title="Soma efetiva dos pagamentos recebidos no mês (alinhado com a aba Receitas).">
                Bruto recebido: <span className="font-semibold">{formatCurrency(resumo.total_recebido_bruto)}</span>
              </p>
            )}
            <div className="text-[10px] text-gray-400 mt-1 space-y-0.5">
              <div className="flex justify-between"><span>Aluguel:</span><span>{formatCurrency(resumo.receita_aluguel)}</span></div>
              {resumo.receita_multa > 0 && (
                <div className="flex justify-between"><span>Multas por atraso:</span><span>{formatCurrency(resumo.receita_multa)}</span></div>
              )}
              {resumo.receita_juros > 0 && (
                <div className="flex justify-between"><span>Juros por atraso:</span><span>{formatCurrency(resumo.receita_juros)}</span></div>
              )}
              {resumo.debitos_receita_efetiva > 0 && (
                <div className="flex justify-between">
                  <span>Débitos/Cobranças extras ({resumo.debitos_percentual_receita || 20}%):</span>
                  <span>{formatCurrency(resumo.debitos_receita_efetiva)}</span>
                </div>
              )}
              {resumo.receita_avulsa > 0 && (
                <div className="flex justify-between"><span>Avulsas:</span><span>{formatCurrency(resumo.receita_avulsa)}</span></div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-600/20 to-red-800/20 border-red-500/30 cursor-pointer hover:border-red-400/50 transition-colors" onClick={() => setDetalheModal('despesas')} data-testid="card-despesas-fixas">
          <CardContent className="p-4">
            <p className="text-xs text-red-300/80">Total Despesas</p>
            <p className="text-xl font-bold text-red-300">{formatCurrency(resumo.total_despesas_fixas)}</p>
            <p className="text-[10px] text-gray-400 mt-1">Despesas fixas ({despesasFixas.length}) definidas pelo admin</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-rose-600/20 to-rose-800/20 border-rose-500/30 cursor-pointer hover:border-rose-400/50 transition-colors" onClick={() => setDetalheModal('manutencoes')} data-testid="card-manutencoes">
          <CardContent className="p-4">
            <p className="text-xs text-rose-300/80">Gasto em Manutenção</p>
            <p className="text-xl font-bold text-rose-300">{formatCurrency(resumo.total_manutencoes)}</p>
            <p className="text-[10px] text-gray-400 mt-1">{manutencoesDetalhe.length} registro(s) no mês</p>
          </CardContent>
        </Card>

        <Card className={`bg-gradient-to-br cursor-pointer transition-colors ${resumo.lucro_liquido >= 0 ? 'from-blue-600/20 to-blue-800/20 border-blue-500/30 hover:border-blue-400/50' : 'from-orange-600/20 to-orange-800/20 border-orange-500/30 hover:border-orange-400/50'}`} onClick={() => setDetalheModal('lucro')}>
          <CardContent className="p-4">
            <p className="text-xs text-blue-300/80">Lucro Líquido</p>
            <p className={`text-xl font-bold ${resumo.lucro_liquido >= 0 ? 'text-blue-300' : 'text-orange-300'}`}>
              {formatCurrency(resumo.lucro_liquido)}
            </p>
            <p className="text-[10px] text-gray-400 mt-1">Receita - Despesas</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-600/20 to-amber-800/20 border-amber-500/30 cursor-pointer hover:border-amber-400/50 transition-colors" onClick={() => setDetalheModal('debitos')}>
          <CardContent className="p-4">
            <p className="text-xs text-amber-300/80">Débitos em Aberto</p>
            <p className="text-xl font-bold text-amber-300">{formatCurrency(resumo.debitos_em_aberto)}</p>
            <p className="text-[10px] text-gray-400 mt-1">Gerados no mês: {formatCurrency(resumo.debitos_gerados)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Despesas Fixas — sub_admin vê somente leitura */}
      <Card className="bg-slate-800/80 border-white/10">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Calendar className="h-4 w-4 text-amber-400" />
              Despesas Fixas Mensais (por moto)
            </CardTitle>
            {!isSubAdmin && (
              <Button
                size="sm"
                data-testid="btn-nova-despesa-fixa"
                onClick={() => { setEditingDespesa(null); setDespesaForm({ nome: '', valor: '', descricao: '' }); setShowDespesaFixaModal(true); }}
                className="bg-amber-600 hover:bg-amber-500 text-white text-xs"
              >
                <Plus className="h-3 w-3 mr-1" /> Nova
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {despesasFixas.length === 0 ? (
            <p className="text-gray-500 text-sm">Nenhuma despesa fixa cadastrada.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {despesasFixas.map(d => (
                <div key={d.id} className="flex items-center gap-2 bg-slate-700/50 border border-white/10 rounded-lg px-3 py-2">
                  <span className="text-sm text-white">{d.nome}</span>
                  <span className="text-sm font-bold text-amber-400">{formatCurrency(d.valor)}</span>
                  {!isSubAdmin && (
                    <>
                      <button onClick={() => { setEditingDespesa(d); setDespesaForm({ nome: d.nome, valor: d.valor, descricao: d.descricao || '' }); setShowDespesaFixaModal(true); }} className="text-gray-400 hover:text-white">
                        <Edit className="h-3 w-3" />
                      </button>
                      <button onClick={() => excluirDespesaFixa(d.id)} className="text-gray-400 hover:text-red-400">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </>
                  )}
                </div>
              ))}
              <div className="flex items-center gap-1 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <span className="text-xs text-amber-300">Total/moto:</span>
                <span className="text-sm font-bold text-amber-400">{formatCurrency(despesasFixas.reduce((a, d) => a + (d.valor || 0), 0))}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista de Motos */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-white">Resultado por Moto</h2>
        {motos.map(moto => {
          const expanded = expandedMotos[moto.moto_id];
          const lucroPct = moto.receita_total > 0 ? ((moto.lucro_liquido / moto.receita_total) * 100).toFixed(0) : 0;

          return (
            <Card key={moto.moto_id} className="bg-slate-800/80 border-white/10 overflow-hidden">
              <div
                className="p-4 cursor-pointer hover:bg-slate-700/30 transition-colors"
                onClick={() => setExpandedMotos(prev => ({ ...prev, [moto.moto_id]: !prev[moto.moto_id] }))}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${moto.lucro_liquido >= 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                      <Bike className={`h-5 w-5 ${moto.lucro_liquido >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
                    </div>
                    <div>
                      <p className="font-bold text-white">{moto.placa}</p>
                      <p className="text-xs text-gray-400">{moto.modelo} {moto.cor}</p>
                      {moto.is_frota_mecanico && (
                        <span className="text-[10px] px-1.5 py-0.5 mt-0.5 inline-block rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 font-bold">FROTA MECÂNICO</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 md:gap-6">
                    <div className="text-right hidden md:block">
                      <p className="text-xs text-gray-400">Receita</p>
                      <p className="text-sm font-semibold text-emerald-400">{formatCurrency(moto.receita_total)}</p>
                    </div>
                    <div className="text-right hidden md:block">
                      <p className="text-xs text-gray-400">Despesas</p>
                      <p className="text-sm font-semibold text-red-400">{formatCurrency(moto.total_despesas)}</p>
                    </div>
                    <div className="text-right hidden md:block">
                      <p className="text-xs text-gray-400">Em aberto</p>
                      <p className="text-sm font-semibold text-amber-400">{formatCurrency(moto.debitos_em_aberto)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Lucro</p>
                      <p className={`text-sm font-bold ${moto.lucro_liquido >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                        {formatCurrency(moto.lucro_liquido)}
                      </p>
                    </div>
                    <div className="hidden md:block">
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${moto.lucro_liquido >= 0 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
                        {lucroPct}%
                      </span>
                    </div>
                    {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                  </div>
                </div>
              </div>

              {expanded && (
                <div className="border-t border-white/5 p-4 bg-slate-900/50">
                  {/* Cards principais em destaque */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-center">
                      <p className="text-[10px] uppercase tracking-wide text-emerald-400/70 font-semibold">Receita</p>
                      <p className="text-lg font-bold text-emerald-300">{formatCurrency(moto.receita_total)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-center">
                      <p className="text-[10px] uppercase tracking-wide text-red-400/70 font-semibold">Despesas</p>
                      <p className="text-lg font-bold text-red-300">{formatCurrency(moto.total_despesas)}</p>
                    </div>
                    <div className={`p-3 rounded-lg text-center border ${moto.lucro_liquido >= 0 ? 'bg-blue-500/10 border-blue-500/30' : 'bg-orange-500/10 border-orange-500/30'}`}>
                      <p className={`text-[10px] uppercase tracking-wide font-semibold ${moto.lucro_liquido >= 0 ? 'text-blue-400/70' : 'text-orange-400/70'}`}>Lucro</p>
                      <p className={`text-lg font-bold ${moto.lucro_liquido >= 0 ? 'text-blue-300' : 'text-orange-300'}`}>{formatCurrency(moto.lucro_liquido)}</p>
                    </div>
                  </div>

                  {/* Detalhamento */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    {/* Receita detalhe */}
                    <div className="p-3 rounded-lg bg-slate-800/50">
                      <p className="text-gray-400 font-medium mb-2 flex items-center gap-1"><TrendingUp className="h-3 w-3 text-emerald-400" /> Composição Receita</p>
                      <p className="text-xs text-gray-500">Locação: {formatCurrency(moto.receita_locacao)}</p>
                      <p className="text-xs text-gray-500">Débitos recebidos: {formatCurrency(moto.debitos_pagos_mes)}</p>
                    </div>
                    {/* Despesas detalhe */}
                    <div className="p-3 rounded-lg bg-slate-800/50">
                      <p className="text-gray-400 font-medium mb-2 flex items-center gap-1"><Wrench className="h-3 w-3 text-red-400" /> Composição Despesas</p>
                      <p className="text-xs text-gray-500">Manutenção: {formatCurrency(moto.despesas_manutencao)}</p>
                      {moto.manutencoes?.map((m, i) => (
                        <p key={i} className="text-[10px] text-gray-600 pl-2">
                          {m.data?.slice(8,10)}/{m.data?.slice(5,7)} — {m.descricao || m.tipo} — {formatCurrency(m.custo_efetivo ?? m.custo)}
                          {m.regra && <span className="text-amber-400 ml-1">({m.regra})</span>}
                        </p>
                      ))}
                      <p className="text-xs text-gray-500 mt-1">Fixas: {formatCurrency(moto.despesas_fixas)}</p>
                    </div>
                    {/* Débitos detalhe */}
                    <div className="p-3 rounded-lg bg-slate-800/50">
                      <p className="text-gray-400 font-medium mb-2 flex items-center gap-1"><DollarSign className="h-3 w-3 text-amber-400" /> Débitos</p>
                      <p className="text-xs text-gray-500">Gerados no mês: {formatCurrency(moto.debitos_gerados_mes)}</p>
                      <p className="text-xs text-gray-500">Pagos no mês: <span className="text-emerald-400">{formatCurrency(moto.debitos_pagos_mes)}</span></p>
                      <p className="text-xs text-gray-500">Em aberto: <span className="text-amber-400 font-bold">{formatCurrency(moto.debitos_em_aberto)}</span></p>
                      {moto.debitos_aberto_lista?.length > 0 && (
                        <div className="mt-1 space-y-0.5 border-t border-white/5 pt-1">
                          {moto.debitos_aberto_lista.map((d, i) => (
                            <p key={i} className="text-[10px] text-gray-600">
                              {d.titulo}: <span className="text-amber-400">{formatCurrency(d.valor_restante)}</span>
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Modal Detalhamento de Indicadores */}
      <Dialog open={!!detalheModal} onOpenChange={() => setDetalheModal(null)}>
        <DialogContent className="bg-slate-800 border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              {detalheModal === 'receita' && 'Detalhamento — Receita Total'}
              {detalheModal === 'despesas' && 'Detalhamento — Despesas'}
              {detalheModal === 'manutencoes' && 'Detalhamento — Manutenções do Mês'}
              {detalheModal === 'lucro' && 'Detalhamento — Lucro Líquido'}
              {detalheModal === 'debitos' && 'Detalhamento — Débitos em Aberto'}
            </DialogTitle>
          </DialogHeader>

          {detalheModal === 'receita' && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex justify-between items-center">
                <span className="text-sm text-gray-300">Receita Total (com regra dos {resumo.debitos_percentual_receita || 20}% sobre débitos)</span>
                <span className="text-lg font-bold text-emerald-300">{formatCurrency(resumo.total_receita)}</span>
              </div>
              {resumo.total_recebido_bruto > 0 && resumo.total_recebido_bruto !== resumo.total_receita && (
                <div className="p-3 rounded-lg bg-slate-700/40 border border-slate-600/40 flex justify-between items-center">
                  <span className="text-sm text-gray-400">Bruto recebido no mês <span className="text-[10px] text-gray-500">(alinha com aba Receitas)</span></span>
                  <span className="text-base font-semibold text-gray-200">{formatCurrency(resumo.total_recebido_bruto)}</span>
                </div>
              )}

              {/* Decomposição por componente */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-300">Decomposição por componente:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 rounded bg-slate-700/30 flex justify-between">
                    <span className="text-gray-400">Aluguel base</span>
                    <span className="text-emerald-400 font-medium">{formatCurrency(resumo.receita_aluguel || 0)}</span>
                  </div>
                  <div className="p-2 rounded bg-slate-700/30 flex justify-between">
                    <span className="text-gray-400">Multas por atraso</span>
                    <span className="text-emerald-400 font-medium">{formatCurrency(resumo.receita_multa || 0)}</span>
                  </div>
                  <div className="p-2 rounded bg-slate-700/30 flex justify-between">
                    <span className="text-gray-400">Juros por atraso</span>
                    <span className="text-emerald-400 font-medium">{formatCurrency(resumo.receita_juros || 0)}</span>
                  </div>
                  <div className="p-2 rounded bg-slate-700/30 flex justify-between">
                    <span className="text-gray-400">Débitos/Cobranças extras ({resumo.debitos_percentual_receita || 20}%)</span>
                    <span className="text-emerald-400 font-medium">{formatCurrency(resumo.debitos_receita_efetiva || 0)}</span>
                  </div>
                  {resumo.receita_avulsa > 0 && (
                    <div className="p-2 rounded bg-slate-700/30 flex justify-between col-span-2">
                      <span className="text-gray-400">Receitas avulsas</span>
                      <span className="text-emerald-400 font-medium">{formatCurrency(resumo.receita_avulsa)}</span>
                    </div>
                  )}
                  {resumo.pagamentos_orfaos > 0 && (
                    <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20 flex justify-between col-span-2">
                      <span className="text-amber-400">Pagamentos sem moto vinculada</span>
                      <span className="text-amber-300 font-medium">{formatCurrency(resumo.pagamentos_orfaos)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-300">Receitas por moto (locações):</p>
                {motos.filter(m => m.receita_locacao > 0).map(m => (
                  <div key={m.moto_id} className="flex justify-between items-center p-2 rounded bg-slate-700/30 text-sm">
                    <span className="text-white font-medium">{m.placa}</span>
                    <span className="text-emerald-400">{formatCurrency(m.receita_locacao)}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center p-2 rounded bg-emerald-500/10 border-t border-emerald-500/20 mt-2 font-semibold">
                  <span className="text-gray-300">Subtotal Locações</span>
                  <span className="text-emerald-300">{formatCurrency(resumo.receita_locacoes)}</span>
                </div>
              </div>
              {resumo.debitos_recebidos > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-300">Débitos/Cobranças extras recebidos no mês:</p>
                  <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20 text-xs text-amber-200">
                    💡 Apenas {resumo.debitos_percentual_receita || 20}% dos <strong>débitos/cobranças extras</strong> (multas de trânsito, taxas, etc) entram como receita/lucro. Os {100 - (resumo.debitos_percentual_receita || 20)}% restantes são considerados ressarcimento de gastos (neutro). <strong>Multas por atraso de pagamento NÃO são débitos</strong> — contam 100% como receita.
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2 rounded bg-slate-700/30 flex justify-between">
                      <span className="text-gray-400">Total bruto recebido</span>
                      <span className="text-amber-300 font-medium">{formatCurrency(resumo.debitos_recebidos)}</span>
                    </div>
                    <div className="p-2 rounded bg-emerald-500/10 flex justify-between">
                      <span className="text-emerald-300">Receita efetiva ({resumo.debitos_percentual_receita || 20}%)</span>
                      <span className="text-emerald-300 font-medium">{formatCurrency(resumo.debitos_receita_efetiva || 0)}</span>
                    </div>
                  </div>

                  {/* Lista detalhada de cada débito pago no mês */}
                  {debitosPagosDetalhe.length > 0 && (
                    <div className="mt-3 border border-slate-700/50 rounded-lg overflow-hidden">
                      <div className="px-3 py-2 bg-slate-800 text-xs font-semibold text-gray-300 flex justify-between">
                        <span>Detalhamento de cada débito pago ({debitosPagosDetalhe.length})</span>
                        <span>Total: {formatCurrency(debitosPagosDetalhe.reduce((s, d) => s + d.valor, 0))}</span>
                      </div>
                      <div className="max-h-64 overflow-y-auto divide-y divide-slate-700/30">
                        {debitosPagosDetalhe.map((d, i) => (
                          <div key={i} className="px-3 py-2 text-xs hover:bg-slate-800/50 flex justify-between items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-gray-400">{d.data?.slice(8,10)}/{d.data?.slice(5,7)}</span>
                                <span className="text-white font-medium">{d.placa}</span>
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${d.origem === 'debito_direto' ? 'bg-amber-500/20 text-amber-300' : 'bg-blue-500/20 text-blue-300'}`}>
                                  {d.origem === 'debito_direto' ? 'DIRETO' : 'PARCELA'}
                                </span>
                              </div>
                              <div className="text-gray-500 truncate mt-0.5">{d.cliente_nome || '-'} • {d.descricao}</div>
                            </div>
                            <span className="text-amber-400 font-semibold whitespace-nowrap">{formatCurrency(d.valor)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {receitasNaoVinculadas.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-amber-300">Receitas sem vínculo a moto ({receitasNaoVinculadas.length}):</p>
                  {receitasNaoVinculadas.slice(0, 15).map((r, i) => (
                    <div key={i} className="flex justify-between items-center p-2 rounded bg-amber-500/5 border border-amber-500/10 text-sm">
                      <span className="text-gray-300">{r.data?.slice(8,10)}/{r.data?.slice(5,7)} — {r.cliente_nome || r.observacoes || r.tipo || 'N/A'}</span>
                      <span className="text-amber-400">{formatCurrency(r.valor)}</span>
                    </div>
                  ))}
                  {receitasNaoVinculadas.length > 15 && <p className="text-xs text-gray-500">...e mais {receitasNaoVinculadas.length - 15} receitas</p>}
                  <div className="flex justify-between items-center p-2 rounded bg-amber-500/10 border-t border-amber-500/20 font-semibold">
                    <span className="text-gray-300">Subtotal Não Vinculadas</span>
                    <span className="text-amber-300">{formatCurrency(resumo.receitas_nao_vinculadas)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {detalheModal === 'despesas' && (
            <div className="space-y-4" data-testid="modal-detalhe-despesas">
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex justify-between items-center">
                <span className="text-sm text-gray-300">Total Despesas Fixas ({mesLabel})</span>
                <span className="text-lg font-bold text-red-300">{formatCurrency(resumo.total_despesas_fixas)}</span>
              </div>
              <p className="text-xs text-gray-400">
                Despesas fixas mensais definidas pelo super admin (ex: Rastreador, Seguro, Marketing),
                aplicadas por moto. Gastos de manutenção têm card próprio.
              </p>
              <div className="space-y-2">
                {despesasFixas.map(d => (
                  <div key={d.id} className="flex justify-between items-center p-2 rounded bg-slate-700/30 text-sm">
                    <span className="text-white">{d.nome}</span>
                    <span className="text-amber-400">{formatCurrency(d.valor)} × {motos.length} motos = {formatCurrency(d.valor * motos.length)}</span>
                  </div>
                ))}
                {despesasFixas.length === 0 && (
                  <p className="text-sm text-gray-500">Nenhuma despesa fixa cadastrada.</p>
                )}
                <div className="flex justify-between items-center p-2 rounded bg-red-500/10 border-t border-red-500/20 font-semibold">
                  <span className="text-gray-300">Total Fixas</span>
                  <span className="text-red-300">{formatCurrency(resumo.total_despesas_fixas)}</span>
                </div>
              </div>
            </div>
          )}

          {detalheModal === 'manutencoes' && (
            <div className="space-y-3" data-testid="modal-detalhe-manutencoes">
              <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 flex justify-between items-center">
                <span className="text-sm text-gray-300">Total gasto em manutenção ({mesLabel})</span>
                <span className="text-lg font-bold text-rose-300">{formatCurrency(resumo.total_manutencoes)}</span>
              </div>
              <p className="text-xs text-gray-400">
                Baseado nos Registros de Manutenção do mês (preventivas + avulsas, todas as motos).
                Este valor já é <strong>descontado no Lucro Líquido</strong>.
              </p>
              {manutencoesDetalhe.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhuma manutenção registrada neste mês.</p>
              ) : (
                <div className="space-y-1.5">
                  {manutencoesDetalhe.map((m, i) => (
                    <div key={`${m.data}-${m.placa}-${i}`} className="p-2.5 rounded-lg bg-slate-700/40 border border-white/5">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-white">{m.placa}</span>
                            {m.avulsa && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 font-semibold uppercase">avulsa</span>
                            )}
                            {m.regra && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300">{m.regra}</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-300 truncate">{m.descricao || m.tipo}</p>
                          <p className="text-[10px] text-gray-500">
                            {String(m.data || '').slice(0, 10)}{m.responsavel ? ` • ${m.responsavel}` : ''}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-sm font-bold text-rose-300">{formatCurrency(m.custo_efetivo ?? m.custo)}</span>
                          {m.custo_efetivo !== undefined && m.custo_efetivo !== m.custo && (
                            <p className="text-[10px] text-gray-500 line-through">{formatCurrency(m.custo)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 border-t border-white/10 text-sm">
                    <span className="text-gray-300 font-semibold">Total ({manutencoesDetalhe.length} registro(s))</span>
                    <span className="text-rose-300 font-bold">{formatCurrency(resumo.total_manutencoes)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {detalheModal === 'lucro' && (
            <div className="space-y-4">
              <div className={`p-3 rounded-lg border flex justify-between items-center ${resumo.lucro_liquido >= 0 ? 'bg-blue-500/10 border-blue-500/20' : 'bg-orange-500/10 border-orange-500/20'}`}>
                <span className="text-sm text-gray-300">Lucro Líquido</span>
                <span className={`text-lg font-bold ${resumo.lucro_liquido >= 0 ? 'text-blue-300' : 'text-orange-300'}`}>{formatCurrency(resumo.lucro_liquido)}</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between p-2 bg-emerald-500/10 rounded">
                  <span className="text-emerald-300">+ Receita Total</span>
                  <span className="text-emerald-300 font-bold">{formatCurrency(resumo.total_receita)}</span>
                </div>
                <div className="flex justify-between p-2 bg-red-500/10 rounded">
                  <span className="text-red-300">- Despesas Fixas</span>
                  <span className="text-red-300 font-bold">{formatCurrency(resumo.total_despesas_fixas)}</span>
                </div>
                <div className="flex justify-between p-2 bg-rose-500/10 rounded">
                  <span className="text-rose-300">- Gasto em Manutenção</span>
                  <span className="text-rose-300 font-bold">{formatCurrency(resumo.total_manutencoes)}</span>
                </div>
                <div className="border-t border-white/10 pt-2 flex justify-between p-2 font-bold">
                  <span className="text-white">= Lucro Líquido</span>
                  <span className={resumo.lucro_liquido >= 0 ? 'text-blue-300' : 'text-orange-300'}>{formatCurrency(resumo.lucro_liquido)}</span>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-300">Lucro por moto:</p>
                {motos.filter(m => m.receita_total > 0 || m.total_despesas > 0).map(m => (
                  <div key={m.moto_id} className="flex justify-between items-center p-2 rounded bg-slate-700/30 text-sm">
                    <span className="text-white font-medium">{m.placa}</span>
                    <span className={m.lucro_liquido >= 0 ? 'text-emerald-400' : 'text-red-400'}>{formatCurrency(m.lucro_liquido)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {detalheModal === 'debitos' && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex justify-between items-center">
                <span className="text-sm text-gray-300">Débitos em Aberto</span>
                <span className="text-lg font-bold text-amber-300">{formatCurrency(resumo.debitos_em_aberto)}</span>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-300">Por moto:</p>
                {motos.filter(m => m.debitos_em_aberto > 0).map(m => (
                  <div key={m.moto_id} className="p-2 rounded bg-slate-700/30">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-white font-medium">{m.placa}</span>
                      <span className="text-amber-400 font-bold">{formatCurrency(m.debitos_em_aberto)}</span>
                    </div>
                    {m.debitos_aberto_lista?.map((d, i) => (
                      <p key={i} className="text-xs text-gray-500 mt-1 pl-2">
                        {d.titulo}: {formatCurrency(d.valor_restante)}
                      </p>
                    ))}
                  </div>
                ))}
                {motos.filter(m => m.debitos_em_aberto > 0).length === 0 && (
                  <p className="text-sm text-gray-500">Nenhum débito em aberto.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Despesa Fixa */}
      <Dialog open={showDespesaFixaModal} onOpenChange={setShowDespesaFixaModal}>
        <DialogContent className="bg-slate-800 border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingDespesa ? 'Editar' : 'Nova'} Despesa Fixa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input
                data-testid="input-despesa-nome"
                value={despesaForm.nome}
                onChange={e => setDespesaForm({ ...despesaForm, nome: e.target.value })}
                placeholder="Ex: Seguro, Rastreador"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <Label>Valor mensal (R$) *</Label>
              <Input
                data-testid="input-despesa-valor"
                type="number"
                step="0.01"
                value={despesaForm.valor}
                onChange={e => setDespesaForm({ ...despesaForm, valor: e.target.value })}
                placeholder="Ex: 45.00"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div>
              <Label>Descrição</Label>
              <Input
                value={despesaForm.descricao}
                onChange={e => setDespesaForm({ ...despesaForm, descricao: e.target.value })}
                placeholder="Opcional"
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 border-white/10" onClick={() => setShowDespesaFixaModal(false)}>Cancelar</Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-500" onClick={salvarDespesaFixa} disabled={!despesaForm.nome || !despesaForm.valor}>
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
